import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/absolute-url";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { MAX_LIST_PAGE, parsePageSearch } from "@/lib/search-params";
import { collectionPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { Header } from "@/components/site/Header";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { Footer } from "@/components/site/Footer";
import { listBlogPosts, listBlogTaxonomyMeta } from "@/lib/blog.functions";
import { authorPageUrl, isPersonAuthor, ORGANIZATION_ID, personNode } from "@/lib/blog-schema";

const metaQO = (slug: string) =>
  queryOptions({
    queryKey: ["blog-author", slug],
    queryFn: () => listBlogTaxonomyMeta({ data: { kind: "author", slug } }),
  });

const postsQO = (slug: string, page: number) =>
  queryOptions({
    queryKey: ["blog-posts", { authorSlug: slug, page }],
    queryFn: () => listBlogPosts({ data: { authorSlug: slug, page } }),
  });

export const Route = createFileRoute("/author/$slug")({
  validateSearch: parsePageSearch,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    if (deps.page > MAX_LIST_PAGE) throw notFound();
    const meta = await context.queryClient.ensureQueryData(metaQO(params.slug));
    if (!meta) throw notFound();
    const posts = await context.queryClient.ensureQueryData(postsQO(params.slug, deps.page));
    // A page past the last one has nothing to show. Answer 404, not an empty page.
    if (deps.page > 1 && posts.posts.length === 0) throw notFound();
    return meta;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const name = loaderData.name;
    return {
      meta: [
        { title: `${name} | RESTORE Hair Blog` },
        { name: "description", content: `Articles by ${name} on the RESTORE Hair blog.` },
      ],
      links: [{ rel: "canonical", href: absoluteUrl(`/author/${params.slug}/`) }],
      scripts: [
        {
          type: "application/ld+json",
          // A real person gets a ProfilePage about that Person. The brand
          // and user-name rows keep the CollectionPage.
          children: safeJsonLd(
            isPersonAuthor(loaderData)
              ? {
                  "@context": "https://schema.org",
                  "@type": "ProfilePage",
                  "@id": authorPageUrl(params.slug),
                  url: authorPageUrl(params.slug),
                  name: `${name} | RESTORE Hair Blog`,
                  isPartOf: { "@id": ORGANIZATION_ID },
                  mainEntity: personNode(loaderData),
                }
              : collectionPageSchema(name, `Articles by ${name} on the RESTORE Hair blog.`, `/author/${params.slug}`),
          ),
        },
      ],
    };
  },
  component: AuthorPage,
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: ({ error, reset }) => (
    <div className="p-10 text-center">Something went wrong.<button onClick={reset} className="ml-2 underline">Retry</button></div>
  ),
});

function AuthorPage() {
  const { slug } = Route.useParams();
  const { page } = Route.useSearch();
  const currentPage = page ?? 1;
  const { data: meta } = useSuspenseQuery(metaQO(slug));
  const { data } = useSuspenseQuery(postsQO(slug, currentPage));
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  if (!meta) return null;

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-12 lg:py-16">
        <header className="mb-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          {meta.avatar_url ? <img src={meta.avatar_url} alt={meta.name} className="h-20 w-20 rounded-full object-cover" /> : null}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">Author</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground lg:text-4xl">{meta.name}</h1>
            {meta.bio ? <p className="mt-2 max-w-2xl text-base text-muted-foreground">{meta.bio}</p> : null}
          </div>
        </header>
        {data.posts.length === 0 ? (
          <p className="text-muted-foreground">No posts by this author yet.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {data.posts.map((p) => (
              <li key={p.id} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg">
                <Link to="/$slug/" params={{ slug: p.slug }} className="flex h-full flex-col">
                  {p.featured_image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-muted">
                      <img src={p.featured_image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col gap-2 p-5">
                    <h2 className="text-lg font-semibold leading-snug text-foreground group-hover:text-primary">{p.title}</h2>
                    {p.excerpt ? <p className="line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p> : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {totalPages > 1 ? (
          <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
            {currentPage > 1 ? <Link to="/author/$slug/" params={{ slug }} search={{ page: currentPage > 2 ? currentPage - 1 : undefined }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">← Newer</Link> : null}
            <span className="px-3 py-2 text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            {currentPage < totalPages ? <Link to="/author/$slug/" params={{ slug }} search={{ page: currentPage + 1 }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">Older →</Link> : null}
          </nav>
        ) : null}
      </main>
      <Footer />
    </>
  );
}